import apiClient from './apiClient';

/**
 * Zeitstempel des letzten erfolgreichen getUnreadCount-Aufrufs (ms seit Epoch).
 * Modul-globale Variable, wird nie exportiert.
 * @type {number}
 */
let lastUnreadCountFetchMs = 0;

/**
 * Prueft ob ein sofortiger Unread-Count-Fetch uebersprungen werden soll,
 * weil der letzte erfolgreiche Fetch weniger als 30 Sekunden zurueckliegt.
 * Verhindert doppelte Backend-Calls bei schnellem Remount (z.B. Routenwechsel).
 *
 * @param {number} [nowMs=Date.now()] - Aktueller Zeitstempel (ueberschreibbar fuer Tests)
 * @returns {boolean} true wenn der Fetch uebersprungen werden soll
 */
export const shouldSkipImmediateUnreadCountFetch = (nowMs = Date.now()) =>
  nowMs - lastUnreadCountFetchMs < 30_000;

/**
 * Service fuer eingehende Mietinteressenten-Anfragen (Inbound Inquiries).
 * Kapselt alle API-Aufrufe gegen /api/rental-advertisements/{id}/inquiries
 * und /api/inquiries.
 */
const inquiryService = {
  /**
   * Liefert alle Anfragen fuer ein bestimmtes Mietinserat.
   * @param {string} advertisementId - UUID des Mietinserats
   * @param {AbortSignal} [signal] - Optionales AbortSignal zum Abbrechen des Requests (Race-Condition-Schutz)
   * @returns {Promise<Array>} Array von InboundInquiryDto
   */
  listForAdvertisement: async (advertisementId, signal) => {
    const response = await apiClient.get(
      `/rental-advertisements/${advertisementId}/inquiries`,
      { signal }
    );
    return response.data;
  },

  /**
   * Liefert eine einzelne Anfrage anhand ihrer ID.
   * @param {string} inquiryId - UUID der Anfrage
   * @returns {Promise<Object>} InboundInquiryDto
   */
  getById: async (inquiryId) => {
    const response = await apiClient.get(`/inquiries/${inquiryId}`);
    return response.data;
  },

  /**
   * Markiert eine Anfrage als gelesen.
   * @param {string} inquiryId - UUID der Anfrage
   * @returns {Promise<void>}
   */
  markRead: async (inquiryId) => {
    await apiClient.post(`/inquiries/${inquiryId}/read`);
  },

  /**
   * Liefert die Anzahl ungelesener Anfragen fuer den aktuellen Benutzer.
   * Bei erfolgreichem Aufruf wird lastUnreadCountFetchMs aktualisiert,
   * damit shouldSkipImmediateUnreadCountFetch korrekt funktioniert.
   * Bei Fehler wird der Zeitstempel NICHT aktualisiert.
   * @returns {Promise<number>} Anzahl ungelesener Anfragen
   */
  getUnreadCount: async () => {
    const response = await apiClient.get('/inquiries/unread-count');
    lastUnreadCountFetchMs = Date.now();
    return response.data.count;
  },

  /**
   * Analysiert den XML-Anhang einer Anfrage erneut (OpenImmo-Parse).
   * Gibt das aktualisierte InboundInquiryDto zurueck.
   * Fehler werden propagiert (kein interner try/catch).
   * @param {string} inquiryId - UUID der Anfrage
   * @returns {Promise<Object>} Aktualisiertes InboundInquiryDto
   */
  reparse: async (inquiryId) => {
    const response = await apiClient.post(`/inquiries/${inquiryId}/reparse`);
    return response.data;
  },

  /**
   * Laedt einen Anhang einer Anfrage herunter und triggert den Browser-Download.
   * @param {string} inquiryId - UUID der Anfrage
   * @param {string} attachmentId - UUID des Anhangs
   * @param {string} fileName - Dateiname fuer den Download
   * @returns {Promise<void>}
   */
  downloadAttachment: async (inquiryId, attachmentId, fileName) => {
    const response = await apiClient.get(
      `/inquiries/${inquiryId}/attachments/${attachmentId}`,
      { responseType: 'blob' }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');

    try {
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
    } finally {
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  },

  /**
   * Sendet eine Antwort auf eine Anfrage als multipart/form-data.
   * Der JSON-Part "request" enthaelt body und optionalen subject.
   * Optionale Datei-Anhaenge werden als "files[]" angehaengt.
   * @param {string} inquiryId - UUID der Anfrage
   * @param {{ body: string, subject?: string|null, files?: File[] }} param
   * @returns {Promise<Object>} Aktualisiertes InboundInquiryDto
   */
  sendReply: async (inquiryId, { body, subject = null, files = [] }) => {
    const formData = new FormData();
    formData.append(
      'request',
      new Blob([JSON.stringify({ body, subject })], { type: 'application/json' })
    );
    files.forEach((file) => formData.append('files', file));

    const response = await apiClient.post(
      `/inquiries/${inquiryId}/replies`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /**
   * Laedt einen Anhang einer Antwort herunter und triggert den Browser-Download.
   * @param {string} inquiryId - UUID der Anfrage
   * @param {string} replyId - UUID der Antwort
   * @param {string} attachmentId - UUID des Anhangs
   * @param {string} fileName - Dateiname fuer den Download
   * @returns {Promise<void>}
   */
  downloadReplyAttachment: async (inquiryId, replyId, attachmentId, fileName) => {
    const response = await apiClient.get(
      `/inquiries/${inquiryId}/replies/${replyId}/attachments/${attachmentId}`,
      { responseType: 'blob' }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');

    try {
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
    } finally {
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  }
};

export default inquiryService;
